import { z } from 'zod';
/**
 * Справочники НСИ (нормативно-справочная информация).
 *
 * Пока два независимых справочника кодов груза — их удобнее обновлять по
 * отдельности, из разных источников:
 * - `gng`   — ГНГ (Гармонизированная номенклатура грузов, ОСЖД/СМГС), 8 знаков;
 * - `etsng` — ЕТСНГ (Единая тарифно-статистическая номенклатура грузов, РЖД), 6 знаков.
 */
export const referenceBookIds = ['gng', 'etsng'];
/** Параметры поиска по справочнику (по коду или наименованию). */
export const referenceSearchQuerySchema = z.object({
    q: z.string().trim().max(200).optional().default(''),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
/**
 * Постраничный просмотр справочника (админ-страницы). В отличие от поиска для
 * формы поручения, здесь нужен весь справочник — страницами и с общим числом.
 */
export const referenceListQuerySchema = z.object({
    q: z.string().trim().max(200).optional().default(''),
    page: z.coerce.number().int().min(1).optional().default(1),
    pageSize: z.coerce.number().int().min(10).max(200).optional().default(50),
});
/** Строка импорта справочника ГНГ (по колонкам официального файла ОСЖД). */
export const gngCodeRowSchema = z.object({
    code: z.string().trim().min(1).max(20),
    name: z.string().trim().max(1000),
    ettClass: z.string().trim().max(10).optional().nullable(),
    position: z.string().trim().max(20).optional().nullable(),
    chapter: z.string().trim().max(20).optional().nullable(),
    section: z.string().trim().max(20).optional().nullable(),
});
/** Строка импорта справочника ЕТСНГ (по колонкам справочника РЖД/Прейскуранта). */
export const etsngCodeRowSchema = z.object({
    code: z.string().trim().min(1).max(20),
    name: z.string().trim().max(1000),
    shortName: z.string().trim().max(200).optional().nullable(),
    level: z.string().trim().max(20).optional().nullable(),
    tariffClass: z.string().trim().max(20).optional().nullable(),
    mvn: z.string().trim().max(50).optional().nullable(),
    dangerous: z.boolean().optional().default(false),
    perishable: z.boolean().optional().default(false),
    section: z.string().trim().max(200).optional().nullable(),
});
/** Импорт справочника ГНГ (чанками: первый — replace, остальные — дозалив). */
export const gngImportSchema = z.object({
    rows: z.array(gngCodeRowSchema).max(5000),
    source: z.string().trim().max(200).optional(),
    replace: z.boolean().optional().default(true),
});
/** Импорт справочника ЕТСНГ. */
export const etsngImportSchema = z.object({
    rows: z.array(etsngCodeRowSchema).max(5000),
    source: z.string().trim().max(200).optional(),
    replace: z.boolean().optional().default(true),
});
/** Строка импорта переходника (по колонкам официального файла). */
export const gngEtsngLinkRowSchema = z.object({
    gngCode: z.string().trim().min(1).max(20),
    etsngCode: z.string().trim().min(1).max(20),
    gngName: z.string().trim().max(1000).optional().nullable(),
    etsngName: z.string().trim().max(1000).optional().nullable(),
    tariffClass: z.string().trim().max(20).optional().nullable(),
    relGngEtsng: z.string().trim().max(10).optional().nullable(),
    relEtsngGng: z.string().trim().max(10).optional().nullable(),
    directions: z.string().trim().max(40).optional().nullable(),
    gngInRed2026: z.string().trim().max(20).optional().nullable(),
    etsngInRzhd: z.boolean().optional().default(false),
});
/** Импорт переходника (чанками). */
export const linkImportSchema = z.object({
    rows: z.array(gngEtsngLinkRowSchema).max(5000),
    source: z.string().trim().max(200).optional(),
    replace: z.boolean().optional().default(true),
});
/**
 * Простые классификаторы НСИ (код + наименование): страны, упаковка, условия
 * поставки. Источники — ФТС. Расширяемо новыми идентификаторами.
 */
export const simpleClassifierIds = [
    'country',
    'packaging',
    'delivery-terms',
    'transport',
    'units',
    'currency',
];
/** Строка импорта простого классификатора. */
export const simpleClassifierRowSchema = z.object({
    code: z.string().trim().min(1).max(40),
    name: z.string().trim().min(1).max(500),
    nameEn: z.string().trim().max(500).optional().nullable(),
    note: z.string().trim().max(1000).optional().nullable(),
});
/** Импорт простого классификатора (чанками). */
export const simpleClassifierImportSchema = z.object({
    rows: z.array(simpleClassifierRowSchema).max(5000),
    source: z.string().trim().max(200).optional(),
    replace: z.boolean().optional().default(true),
});
//# sourceMappingURL=reference-data.js.map